import React, { useEffect, useRef } from "react";
import { User } from "lucide-react";
import { cn } from "lib/utils";
import { deriveStepState, BUILDER_STEPS } from "../state/stepState";
import type { BuilderCampaignSnapshot, BuilderStep, BuilderStepProps, ChannelConfig, StepCompletion } from "../config";
import { Card, Skeleton } from "../ui";
import "../ui/theme.css";
import CopilotLauncher from "copilot/CopilotLauncher";
import { StepHeader } from "./StepHeader";
import { ThemeToggle } from "./ThemeToggle";
import { SummaryCard, type SaveStatus } from "./SummaryCard";
import { LaunchDateBanner } from "./LaunchDateBanner";

export interface BuilderSummary {
  totalCost: string;
  campaignName: string;
  launchDate: string;
  audiences: string;
  format: string;
  saveStatus?: SaveStatus;
  warning?: React.ReactNode;
  launchDateWarning?: boolean;
  /** Per-step Copilot CTA anchor — see `SummaryCardProps.ctaDataCy`. */
  ctaDataCy?: string;
}

export interface BuilderShellProps {
  config: ChannelConfig;
  campaign: BuilderCampaignSnapshot;
  activeStep: BuilderStep;
  onStepChange: (step: BuilderStep) => void;
  summary: BuilderSummary;
  onPrimaryAction: () => void;
  onRequestValidation: (step: BuilderStep) => void;
  isSubmitBlocked?: boolean;
  isLoading?: boolean;
  contentReadOnly?: boolean;
  onEditLaunchDate?: () => void;
  stepComponents?: Partial<Record<BuilderStep, React.ComponentType<BuilderStepProps>>>;
  /** Slot rendered at the right of the header (e.g. the account menu). */
  headerRight?: React.ReactNode;
  /** Banner pinned above the step body on every step. */
  topBanner?: React.ReactNode;
  /** Step-completion overrides folded into reachability + advancement (F8). */
  completionOverride?: Partial<StepCompletion>;
  /**
   * Disables the active-step CTA with a hover tooltip — the Targeting min-gate
   * (AC31), the sole disabled-CTA case. Distinct from the final-step submit block.
   */
  primaryActionBlock?: { disabled?: boolean; tooltip?: { title: string; message: string } };
}

const StepPlaceholder: React.ComponentType<BuilderStepProps> = () => (
  <Card className="text-muted-foreground p-6 text-sm">This step is not available yet.</Card>
);

const StepSkeleton = () => (
  <div className="flex flex-col gap-4">
    <Skeleton className="h-40 w-full" />
    <Skeleton className="h-40 w-full" />
  </div>
);

const BuilderShell = ({
  config,
  campaign,
  activeStep,
  onStepChange,
  summary,
  onPrimaryAction,
  onRequestValidation,
  isSubmitBlocked,
  isLoading,
  contentReadOnly,
  onEditLaunchDate,
  stepComponents,
  headerRight,
  completionOverride,
  primaryActionBlock,
  topBanner,
}: BuilderShellProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const { completion, reachable } = deriveStepState(campaign, config, completionOverride);
  const isFinalStep = activeStep === "tracking";
  const ctaDisabled = (isFinalStep && !!isSubmitBlocked) || !!primaryActionBlock?.disabled;

  useEffect(() => {
    const touched: { node: HTMLElement; prev: string }[] = [];
    const pin = (node: HTMLElement | null | undefined) => {
      if (!node) return;
      touched.push({ node, prev: node.style.height });
      node.style.height = "100vh";
    };
    let el = rootRef.current?.parentElement ?? null;
    while (el) {
      const overflowY = getComputedStyle(el).overflowY;
      if (overflowY === "scroll" || overflowY === "auto") {
        pin(el);
        pin(el.parentElement);
        break;
      }
      el = el.parentElement;
    }
    return () => touched.forEach(({ node, prev }) => (node.style.height = prev));
  }, []);

  const handlePrimaryAction = () => {
    if (!completion[activeStep]) {
      onRequestValidation(activeStep);
      return;
    }
    if (isFinalStep) {
      if (isSubmitBlocked) return;
      onPrimaryAction();
      return;
    }
    const next = BUILDER_STEPS[BUILDER_STEPS.indexOf(activeStep) + 1];
    if (next) onStepChange(next);
  };

  const ActiveStep =
    activeStep === "targeting"
      ? stepComponents?.targeting ?? config.TargetingStep
      : stepComponents?.[activeStep] ?? StepPlaceholder;

  const unitsValue = config.summaryUnits.getValue(campaign);
  const formattedUnits = config.summaryUnits.format ? config.summaryUnits.format(unitsValue) : String(unitsValue);

  return (
    // App-shell layout: a fixed-height column with a non-scrolling header and a
    // single scrollable content region. The header is a flex child (not `sticky`),
    // so it can never drift on momentum/overscroll. `h-full` resolves against the
    // shared <main> (pinned to 100vh by the effect above), so the content region —
    // not the page — owns the scroll.
    <div ref={rootRef} className="cb-v2-root flex h-full flex-col bg-page">
      <header className="z-20 flex h-[72px] shrink-0 items-center border-b border-border bg-background px-6">
        <div className="flex flex-1 justify-center">
          <StepHeader
            activeStep={activeStep}
            completion={completion}
            reachable={reachable}
            onStepSelect={onStepChange}
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          <CopilotLauncher />
          <ThemeToggle />
          {headerRight ?? (
            <span
              aria-hidden="true"
              className="bg-slate-200 dark:bg-accent text-muted-foreground flex h-9 w-9 items-center justify-center rounded-full"
            >
              <User className="h-5 w-5" />
            </span>
          )}
        </div>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl px-6 pb-10 pt-6">
          <h1 className="mb-6 text-[24px] font-semibold leading-[32px] tracking-[-0.6px] text-[#222336] dark:text-foreground">
            {`New ${config.label} campaign`}
          </h1>
          {topBanner && <div className="mb-6">{topBanner}</div>}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
            <fieldset
              disabled={contentReadOnly}
              className={cn("m-0 flex min-w-0 flex-col gap-4 border-0 p-0", contentReadOnly && "pointer-events-none")}
            >
              {isLoading ? <StepSkeleton /> : <ActiveStep campaign={campaign} />}
            </fieldset>
            <aside>
              <div className="sticky top-6 flex flex-col gap-4">
                <SummaryCard
                  activeStep={activeStep}
                  totalCost={summary.totalCost}
                  campaignName={summary.campaignName}
                  launchDate={summary.launchDate}
                  audiences={summary.audiences}
                  unitsLabel={config.summaryUnits.label}
                  unitsValue={formattedUnits}
                  format={summary.format}
                  saveStatus={summary.saveStatus}
                  launchDateWarning={summary.launchDateWarning}
                  primaryActionDisabled={ctaDisabled}
                  primaryActionTooltip={primaryActionBlock?.disabled ? primaryActionBlock.tooltip : undefined}
                  ctaDataCy={summary.ctaDataCy}
                  onPrimaryAction={handlePrimaryAction}
                />
                {summary.launchDateWarning && activeStep !== "targeting" && onEditLaunchDate && (
                  <LaunchDateBanner onEditDate={onEditLaunchDate} />
                )}
                {summary.warning}
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
};

export { BuilderShell };
